import { useState, useCallback } from "react";
import StepOneForm from "../components/StepOneForm";
import StepTwoVerify from "../components/StepTwoVerify";
import StepThreeFAQ from "../components/StepThreeFAQ";
import StepFourBrand from "../components/StepFourBrand";
import StepFiveFeatures from "../components/StepFiveFeatures";
import ThankYouPage from "../components/ThankYouPage";
import CancelConfirmationModal from "../components/CancelConfirmationModal";
import { onboardingService } from "../utils/googlePlaces";
import OnboardingErrorPage from "../components/OnboardingErrorPage";

export default function OnboardingApp() {
  // Extract query string parameter 'g' for business group
  const [businessGroupName] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('g') || 'self'; // Default to 'self' if not provided
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isPhoneLoading, setIsPhoneLoading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [phoneOtpVerified, setPhoneOtpVerified] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpVerified, setEmailOtpVerified] = useState(false);
  const [isTraining, setIsTraining] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isStepOneValid, setIsStepOneValid] = useState(false);
  const [emailOtpError, setEmailOtpError] = useState("");
  const [phoneOtpError, setPhoneOtpError] = useState("");
  const [emailExistsError, setEmailExistsError] = useState("");
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [errorState, setErrorState] = useState({
  hasError: false,
  errorMessage: "",
  errorDetails: null,
  previousStep: 5
});

  const [formData, setFormData] = useState({
    businessName: "",
    websiteAddress: "",
    email: "",
    phoneNumber: "",
    otp: "",
    emailOtp: "",
    extractedInfo: null,
    faqs: [],
    agentName: "",
    greeting: "",
    recordDisclaimer: "This call may be recorded for quality assurance purposes.",
    notificationEmails: "",
    notificationOptions: {
      email: false,
      sms: false,
      whatsapp: false,
      disclaimerEnabled: true,
    },
    features: {
      takingMessages: true,
      bookAppointments: true,
      appointmentReminder: true,
      transferCall: false,
    },
    // Google Business Profile tracking
    isGBP: false, // Boolean flag for Google Business Profile found/selected
    googleBusinessProfile: null, // Complete Google Business Profile data
  });

  // Error handling functions
const handleError = (errorMessage, errorDetails = null, previousStep = 5) => {
  console.error('Onboarding Error:', errorMessage, errorDetails);
  setErrorState({
    hasError: true,
    errorMessage,
    errorDetails,
    previousStep
  });
  setCurrentStep(7); // Step 7 will be our error page
};

const handleTryAgain = () => {
  // Clear error state and return to previous step
  setErrorState({
    hasError: false,
    errorMessage: "",
    errorDetails: null,
    previousStep: 5
  });
  setCurrentStep(errorState.previousStep);
};

const handleGoBack = () => {
  // Clear error state and go back one step from where error occurred
  setErrorState({
    hasError: false,
    errorMessage: "",
    errorDetails: null,
    previousStep: 5
  });
  setCurrentStep(Math.max(1, errorState.previousStep - 1));
};

const handleStartOver = () => {
  // Reset everything to initial state
  setErrorState({
    hasError: false,
    errorMessage: "",
    errorDetails: null,
    previousStep: 5
  });
  handleConfirmCancel(); // Use existing reset logic
};

  const handleInputChange = useCallback((field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  }, []);

  const handleEmailVerification = async (email, setEmailError) => {
  if (!email || !validateEmail(email)) {
    setEmailError("Please enter a valid email address.");
    return;
  }

  setIsCheckingEmail(true);
  setEmailError("");
  
  try {
    const result = await onboardingService.verifyEmailAndSendOTP(email);
    // console.log('Email verification result:', result);
    
    if (result.success) {
      // User doesn't exist and OTP was sent successfully
      setEmailOtpSent(true);
      setEmailError("");
    } else {
      // Check the specific error type based on the step or userExists flag
      if (result.userExists === true || result.step === 'user_exists') {
        // User already exists
        setEmailError(result.error);
      } else if (result.step === 'validation' || result.error?.includes('Invalid email format')) {
        // Invalid email format
        setEmailError("Please enter a valid email address.");
      } else if (result.step === 'otp_send') {
        // OTP sending failed
        setEmailError("Failed to send verification email. Please try again.");
      } else {
        // Generic error
        setEmailError(result.error || "Unable to verify email address. Please try again.");
      }
    }
    
  } catch (error) {
    console.error('Error during email verification:', error);
    setEmailError("Unable to verify email address. Please try again.");
  } finally {
    setIsCheckingEmail(false);
  }
};

 

// Clear email exists error when clearing other errors
const clearEmailExistsError = () => {
  setEmailExistsError("");
};

  // Add these functions to clear errors
const clearEmailOtpError = () => {
  setEmailOtpError("");
};

const clearPhoneOtpError = () => {
  setPhoneOtpError("");
};
  // Separate callback for validation status
  const handleValidationChange = useCallback((isValid) => {
    setIsStepOneValid(isValid);
  }, []);

  // Helper function to generate business overview
  const generateBusinessOverview = (businessData) => {
    // Use editorial summary if available, otherwise generate
    if (businessData.editorialSummary) {
      return businessData.editorialSummary;
    }
    
    const parts = [];
    
    if (businessData.businessName) {
      parts.push(`${businessData.businessName} is a`);
    }
    
    if (businessData.businessTypes && businessData.businessTypes.length > 0) {
      const primaryType = businessData.businessTypes[0];
      const typeDescription = formatBusinessType(primaryType);
      parts.push(typeDescription);
    }
    
    if (businessData.address) {
      parts.push(`located at ${businessData.address}`);
    }
    
    if (businessData.rating && businessData.totalReviews) {
      parts.push(`with a ${businessData.rating}-star rating from ${businessData.totalReviews} customer reviews`);
    }
    
    return parts.join(' ') + '.';
  };

  // Helper function to generate services from business types
  const generateServicesFromTypes = (businessTypes) => {
    if (!businessTypes || businessTypes.length === 0) return [];
    
    const serviceMap = {
      'restaurant': ['Dining Services', 'Food & Beverage', 'Customer Service'],
      'food': ['Food Services', 'Catering', 'Takeaway'],
      'store': ['Retail Services', 'Product Sales', 'Customer Support'],
      'health': ['Health Services', 'Medical Consultations', 'Wellness Programs'],
      'beauty_salon': ['Beauty Services', 'Hair Styling', 'Beauty Consultations'],
      'gym': ['Fitness Services', 'Personal Training', 'Membership Services'],
      'lawyer': ['Legal Services', 'Legal Consultations', 'Legal Advice'],
      'dentist': ['Dental Services', 'Dental Consultations', 'Dental Care'],
      'hospital': ['Medical Services', 'Emergency Care', 'Patient Services'],
      'school': ['Educational Services', 'Learning Programs', 'Student Support'],
      'bank': ['Banking Services', 'Financial Services', 'Customer Support'],
      'real_estate_agency': ['Real Estate Services', 'Property Sales', 'Property Consultations'],
      // Additional mappings for better coverage
      'establishment': ['Professional Services', 'Customer Support'],
      'point_of_interest': ['Information Services', 'Customer Assistance'],
      'finance': ['Financial Services', 'Financial Consulting', 'Customer Support'],
      'accounting': ['Accounting Services', 'Tax Preparation', 'Financial Planning'],
      'insurance_agency': ['Insurance Services', 'Policy Management', 'Claims Support'],
      'car_dealer': ['Vehicle Sales', 'Trade-ins', 'Financing'],
      'car_repair': ['Vehicle Maintenance', 'Repairs', 'Diagnostics'],
      'gas_station': ['Fuel Service', 'Car Wash', 'Convenience Items'],
      'electronics_store': ['Electronics Sales', 'Technical Support', 'Product Consultation']
    };
    
    const services = new Set();
    businessTypes.forEach(type => {
      if (serviceMap[type]) {
        serviceMap[type].forEach(service => services.add(service));
      }
    });
    
    return services.size > 0 ? Array.from(services) : ['General Business Services', 'Customer Support', 'Professional Services'];
  };

  // Helper function to determine industry
  const determineIndustry = (businessTypes) => {
    if (!businessTypes || businessTypes.length === 0) return 'General Business';
    
    const industryMap = {
      'restaurant': 'Food & Beverage',
      'food': 'Food & Beverage', 
      'store': 'Retail',
      'health': 'Healthcare',
      'beauty_salon': 'Beauty & Wellness',
      'gym': 'Fitness & Wellness',
      'lawyer': 'Legal Services',
      'dentist': 'Healthcare',
      'hospital': 'Healthcare',
      'school': 'Education',
      'bank': 'Financial Services',
      'real_estate_agency': 'Real Estate',
      'lodging': 'Hospitality',
      'gas_station': 'Automotive Services',
      'car_repair': 'Automotive Services',
      // Additional mappings
      'establishment': 'Professional Services',
      'point_of_interest': 'General Business',
      'finance': 'Financial Services',
      'accounting': 'Professional Services - Accounting',
      'insurance_agency': 'Financial Services - Insurance',
      'car_dealer': 'Automotive - Sales',
      'electronics_store': 'Retail - Electronics'
    };
    
    for (const type of businessTypes) {
      if (industryMap[type]) {
        return industryMap[type];
      }
    }
    
    return 'General Business';
  };

  // Helper function to format business types
  const formatBusinessType = (type) => {
    const typeMap = {
      'restaurant': 'restaurant',
      'food': 'food establishment', 
      'store': 'retail store',
      'health': 'healthcare provider',
      'beauty_salon': 'beauty salon',
      'gym': 'fitness center',
      'lawyer': 'law firm',
      'dentist': 'dental practice',
      'hospital': 'medical facility',
      'school': 'educational institution',
      'bank': 'financial institution',
      'real_estate_agency': 'real estate agency',
      // Additional mappings
      'establishment': 'business',
      'point_of_interest': 'business',
      'finance': 'financial services provider',
      'accounting': 'accounting firm',
      'insurance_agency': 'insurance agency',
      'car_dealer': 'car dealership',
      'electronics_store': 'electronics store'
    };
    
    return typeMap[type] || 'business';
  };

  // Helper function to generate FAQs
  const generateFAQsFromBusiness = (businessData) => {
    const faqs = [];
    
    // Contact FAQ
    if (businessData.phone || businessData.website) {
      faqs.push({
        question: `How can I contact ${businessData.businessName}?`,
        answer: `You can reach us${businessData.phone ? ` by phone at ${businessData.phone}` : ''}${businessData.phone && businessData.website ? ' or' : ''}${businessData.website ? ` visit our website at ${businessData.website}` : ''}.`
      });
    }
    
    // Location FAQ
    if (businessData.address) {
      faqs.push({
        question: `Where is ${businessData.businessName} located?`,
        answer: `We are located at ${businessData.address}.`
      });
    }
    
    // Hours FAQ
    if (businessData.openingHours && businessData.openingHours.weekdayText) {
      faqs.push({
        question: `What are your opening hours?`,
        answer: `Our opening hours are: ${businessData.openingHours.weekdayText}.`
        // answer: `Our opening hours are: ${businessData.openingHours.weekdayText.slice(0, 2).join(', ')}.`
      });
    }
    
    // Services FAQ (generic)
    if (businessData.businessTypes && businessData.businessTypes.length > 0) {
      const services = generateServicesFromTypes(businessData.businessTypes);
      faqs.push({
        question: `What services do you offer?`,
        answer: `We provide ${services.slice(0, 3).join(', ')} and more.`
      });
    }
    
    return faqs;
  };

  // Helper function to generate greeting
  const generateGreeting = (businessName) => {
    // console.log('--- generateGreeting ---');
    return `Hello! Welcome to ${businessName}. How can I help you today?`;
  };

  // Website URL validation function
  const isValidUrl = (url) => {
    if (!url || url.trim() === '') return false;
    
    const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
    
    if (!urlRegex.test(url)) {
      return false;
    }

    try {
      const prefixedUrl = url.startsWith('http') ? url : `https://${url}`;
      new URL(prefixedUrl);
      return true;
    } catch (e) {
      return false;
    }
  };

  // UPDATED: Validation function for step one with GBP-aware logic
  const validateStepOne = () => {
    // If GBP is selected, only need OTPs verified
    if (formData.isGBP && formData.googleBusinessProfile) {
      return phoneOtpVerified && emailOtpVerified;
    }
    
    // If no GBP, need valid website address
    const isWebsiteValid = formData.websiteAddress.trim() !== '' && isValidUrl(formData.websiteAddress);
    return phoneOtpVerified && emailOtpVerified && isWebsiteValid;
  };

  // Replace the mock OTP functions with real API calls
  async function handleSendEmailOTP() {
    if (!formData.email) return;
    setIsEmailLoading(true);
    
    try {
      const result = await onboardingService.sendEmailOTP(formData.email);
      if (result.success) {
        setEmailOtpSent(true);
      } else {
        console.error('Failed to send email OTP:', result.error);
        // You can show an error message to the user here
      }
    } catch (error) {
      console.error('Error sending email OTP:', error);
    } finally {
      setIsEmailLoading(false);
    }
  }
  async function handleSendEmailOTP_1() {
    if (!formData.email) return;
    setIsEmailLoading(true);
    setTimeout(() => {
      setIsEmailLoading(false);
      setEmailOtpSent(true);
    }, 2000);
  }

  async function handleVerifyEmailOTP() {
  if (!formData.emailOtp) return;
  setIsEmailLoading(true);
  setEmailOtpError(""); // Clear previous errors
  
  try {
    const result = await onboardingService.verifyEmailOTP(formData.email, formData.emailOtp);
    if (result.success) {
      setEmailOtpVerified(true);
      setEmailOtpError("");
    } else {
      setEmailOtpError(result.error || "Invalid OTP. Please try again.");
      console.error('Failed to verify email OTP:', result.error);
    }
  } catch (error) {
    setEmailOtpError("Verification failed. Please try again.");
    console.error('Error verifying email OTP:', error);
  } finally {
    setIsEmailLoading(false);
  }
}

  async function handleVerifyEmailOTP_1() {
    if (!formData.emailOtp) return;
    setIsEmailLoading(true);
    setTimeout(() => {
      setIsEmailLoading(false);
      setEmailOtpVerified(true);
    }, 1500);
  }

  async function handleSendPhoneOTP() {
  if (!formData.phoneNumber) return;
  setIsPhoneLoading(true);
  
  try {
    const result = await onboardingService.sendSMSOTP(formData.phoneNumber);
    if (result.success) {
      setPhoneOtpSent(true);
      // For development, log the debug OTP
      if (result.debugOtp) {
        // console.log('Debug SMS OTP:', result.debugOtp);
      }
    } else {
      console.error('Failed to send phone OTP:', result.error);
    }
  } catch (error) {
    console.error('Error sending phone OTP:', error);
  } finally {
    setIsPhoneLoading(false);
  }
}
  async function handleSendPhoneOTP_notusedif() {
    if (!formData.phoneNumber) return;
    setIsPhoneLoading(true);
    setTimeout(() => {
      setIsPhoneLoading(false);
      setPhoneOtpSent(true);
    }, 2000);
  }

  // Update handleVerifyPhoneOTP function
async function handleVerifyPhoneOTP() {
  if (!formData.otp) return;
  setIsPhoneLoading(true);
  setPhoneOtpError(""); // Clear previous errors
  
  try {
    const result = await onboardingService.verifySMSOTP(formData.phoneNumber, formData.otp);
    if (result.success) {
      setPhoneOtpVerified(true);
      setPhoneOtpError("");
    } else {
      setPhoneOtpError(result.error || "Invalid OTP. Please try again.");
      console.error('Failed to verify phone OTP:', result.error);
    }
  } catch (error) {
    setPhoneOtpError("Verification failed. Please try again.");
    console.error('Error verifying phone OTP:', error);
  } finally {
    setIsPhoneLoading(false);
  }
}
  async function handleVerifyPhoneOTP_notusedif() {
    if (!formData.otp) return;
    setIsPhoneLoading(true);
    setTimeout(() => {
      setIsPhoneLoading(false);
      setPhoneOtpVerified(true);
    }, 1500);
  }

  // This function is called when user clicks "Next Step" button in Step 1
async function handleTrainAgent(extractedInfo = null) {
  // First validate that step one requirements are met
  if (!validateStepOne()) {
    console.error("Cannot proceed: Step one validation failed");
    return;
  }

  setIsTraining(true);
  
  // FIXED: Use the passed extractedInfo immediately
  const finalExtractedInfo = extractedInfo || {
    businessName: formData.businessName ,
    website: formData.isGBP && formData.googleBusinessProfile?.website 
      ? formData.googleBusinessProfile.website 
      : formData.websiteAddress ,
    industry: formData.isGBP && formData.googleBusinessProfile 
      ? formData.googleBusinessProfile.industry 
      : "",
    description: formData.isGBP && formData.googleBusinessProfile 
      ? formData.googleBusinessProfile.businessOverview 
      : "",
    services: formData.isGBP && formData.googleBusinessProfile 
      ? formData.googleBusinessProfile.services 
      : [],
    // Additional fields for AI training
    editorialSummary: formData.isGBP && formData.googleBusinessProfile?.editorialSummary || null,
    formattedPhone: formData.isGBP && formData.googleBusinessProfile?.formattedPhoneNumber || formData.phoneNumber,
    internationalPhone: formData.isGBP && formData.googleBusinessProfile?.internationalPhoneNumber || formData.phoneNumber,
    businessTypes: formData.isGBP && formData.googleBusinessProfile?.businessTypes || [],
    openingHours: formData.isGBP && formData.googleBusinessProfile?.openingHours || null,
    rating: formData.isGBP && formData.googleBusinessProfile?.rating || null,
    totalReviews: formData.isGBP && formData.googleBusinessProfile?.totalReviews || null,
    address: formData.isGBP && formData.googleBusinessProfile?.address || null,
    businessStatus: formData.isGBP && formData.googleBusinessProfile?.businessStatus || null,
    priceLevel: formData.isGBP && formData.googleBusinessProfile?.priceLevel || null
  };

   let dynamicFAQs = [];
  let dynamicGreeting = "";
   if (formData.isGBP && formData.googleBusinessProfile) {
    // Use GBP data for dynamic generation
    const businessData = {
      businessName: formData.googleBusinessProfile.businessName || formData.businessName,
      phone: formData.googleBusinessProfile.phone || formData.googleBusinessProfile.formattedPhoneNumber,
      website: formData.googleBusinessProfile.website || formData.websiteAddress,
      address: formData.googleBusinessProfile.address,
      openingHours: formData.googleBusinessProfile.openingHours,
      businessTypes: formData.googleBusinessProfile.businessTypes,
      rating: formData.googleBusinessProfile.rating,
      totalReviews: formData.googleBusinessProfile.totalReviews
    };
    
    dynamicFAQs = generateFAQsFromBusiness(businessData);
    dynamicGreeting = generateGreeting(businessData.businessName);
  } else {
    // Use extracted info for dynamic generation
    const businessData = {
      businessName: finalExtractedInfo.businessName,
      phone: finalExtractedInfo.formattedPhone || formData.phoneNumber,
      website: finalExtractedInfo.website || formData.websiteAddress,
      address: finalExtractedInfo.address,
      openingHours: finalExtractedInfo.openingHours,
      businessTypes: finalExtractedInfo.businessTypes,
      rating: finalExtractedInfo.rating,
      totalReviews: finalExtractedInfo.totalReviews
    };
    
    dynamicFAQs = generateFAQsFromBusiness(businessData);
    dynamicGreeting = generateGreeting(businessData.businessName);
  }

    // Fallback to default FAQs if none generated
    if (dynamicFAQs.length === 0) {
      dynamicFAQs = [
        {
          question: `What services does ${finalExtractedInfo.businessName} offer?`,
          answer: `We provide ${Array.isArray(finalExtractedInfo.services) ? finalExtractedInfo.services.slice(0, 3).join(', ') : 'professional services'}.`
        },
        {
          question: `How can I contact ${finalExtractedInfo.businessName}?`,
          answer: `You can reach us via email or by phone at ${formData.phoneNumber}.`
        }
      ];
    }

  setTimeout(() => {
    setFormData((prev) => ({
      ...prev,
      extractedInfo: finalExtractedInfo,
      faqs: dynamicFAQs, // Use dynamically generated FAQs
      agentName: "Alex",
      greeting: dynamicGreeting || "Hello! How can I help you today?", // Use dynam
      notificationEmails: prev.email,
      notificationOptions: {
        ...prev.notificationOptions,
        email: true,
        disclaimerEnabled: prev.notificationOptions.disclaimerEnabled,
      },
    }));
    setIsTraining(false);
    setCurrentStep(2);
  }, 3000);
}
 
const handleFinalSubmit = async () => {
  // console.log('=== FINAL FORM SUBMISSION - COMPLETE DATA ===');
  console.log('Full formData object:', JSON.stringify(formData, null, 2));
  console.log('Business Group Name:', businessGroupName);

  const apiPayload = {
    // Basic business info
    businessName: formData.businessName,
    websiteAddress: formData.websiteAddress,
    email: formData.email,
    phoneNumber: formData.phoneNumber,

    // Business Group Name from query string
    businessGroupName: businessGroupName,

    // Google Business Profile flag and data
    isGBP: formData.isGBP,
    googleBusinessProfile: formData.isGBP ? {
      placeId: formData.googleBusinessProfile?.placeId,
      businessName: formData.googleBusinessProfile?.businessName,
      address: formData.googleBusinessProfile?.address,
      phone: formData.googleBusinessProfile?.phone,
      internationalPhone: formData.googleBusinessProfile?.internationalPhone,
      website: formData.googleBusinessProfile?.website,
      industry: formData.googleBusinessProfile?.industry,
      businessOverview: formData.googleBusinessProfile?.businessOverview,
      services: formData.googleBusinessProfile?.services,
      rating: formData.googleBusinessProfile?.rating,
      totalReviews: formData.googleBusinessProfile?.totalReviews,
      businessTypes: formData.googleBusinessProfile?.businessTypes,
      editorialSummary: formData.googleBusinessProfile?.editorialSummary,
      formattedPhoneNumber: formData.googleBusinessProfile?.formattedPhoneNumber,
      internationalPhoneNumber: formData.googleBusinessProfile?.internationalPhoneNumber,
      openingHours: formData.googleBusinessProfile?.openingHours,
      businessStatus: formData.googleBusinessProfile?.businessStatus,
      priceLevel: formData.googleBusinessProfile?.priceLevel,
      location: formData.googleBusinessProfile?.location,
      generatedFAQs: formData.googleBusinessProfile?.generatedFAQs,
      generatedGreeting: formData.googleBusinessProfile?.generatedGreeting
    } : null,

    // Agent configuration
    agentName: formData.agentName,
    greeting: formData.greeting,
    recordDisclaimer: formData.recordDisclaimer,

    // Features and notifications
    features: formData.features,
    notificationOptions: formData.notificationOptions,
    notificationEmails: formData.notificationEmails,

    // Custom FAQs
    faqs: formData.faqs,

    // Processed info with all available data
    extractedInfo: formData.extractedInfo
  };
  
  console.log('API Payload (for database save):', JSON.stringify(apiPayload, null, 2));
  console.log('=== END FINAL SUBMISSION LOG ===');
  
  setIsPhoneLoading(true);

  try {
      const result = await onboardingService.saveBusinessOnboarding(apiPayload);
      
      if (result.success) {
        console.log('Onboarding saved successfully:', result.data);
        setCurrentStep(6);
      } else {
        console.error('Failed to save onboarding:', result.error);
        // Handle error with our new error handling system
        handleError(
          result.error || "Failed to complete onboarding. Please try again.",
          result,
          5 // Current step is 5 (features step)
        );
      }
    } catch (error) {
      console.error('Error during final submission:', error);
      // Handle network or unexpected errors
      const errorMessage = error.message?.includes('network') || error.message?.includes('fetch')
        ? "Network connection error. Please check your internet connection and try again."
        : "An unexpected error occurred during submission. Please try again.";
        
      handleError(
        errorMessage,
        {
          originalError: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        },
        5
      );
    } finally {
      setIsPhoneLoading(false);
    }
};

  const handleFinalSubmit_28Aug = async () => {
    console.log('=== FINAL FORM SUBMISSION - COMPLETE DATA ===');
    console.log('Full formData object:', JSON.stringify(formData, null, 2));
    
    console.log('=== BUSINESS INFORMATION ===');
    console.log('Business Name:', formData.businessName);
    console.log('Website Address:', formData.websiteAddress);
    console.log('Is GBP (Google Business Profile):', formData.isGBP);
    
    if (formData.isGBP && formData.googleBusinessProfile) {
      console.log('=== GOOGLE BUSINESS PROFILE DATA ===');
      console.log('Place ID:', formData.googleBusinessProfile.placeId);
      console.log('Business Name (GBP):', formData.googleBusinessProfile.businessName);
      console.log('Address (GBP):', formData.googleBusinessProfile.address);
      console.log('Phone (GBP):', formData.googleBusinessProfile.phone);
      console.log('International Phone (GBP):', formData.googleBusinessProfile.internationalPhone);
      console.log('Website (GBP):', formData.googleBusinessProfile.website);
      console.log('Rating:', formData.googleBusinessProfile.rating);
      console.log('Total Reviews:', formData.googleBusinessProfile.totalReviews);
      console.log('Business Types:', formData.googleBusinessProfile.businessTypes);
      console.log('Editorial Summary:', formData.googleBusinessProfile.editorialSummary);
      console.log('Formatted Phone Number:', formData.googleBusinessProfile.formattedPhoneNumber);
      console.log('International Phone Number:', formData.googleBusinessProfile.internationalPhoneNumber);
      console.log('Industry (Generated):', formData.googleBusinessProfile.industry);
      console.log('Business Overview (Generated):', formData.googleBusinessProfile.businessOverview);
      console.log('Services (Generated):', formData.googleBusinessProfile.services);
      console.log('Generated FAQs:', formData.googleBusinessProfile.generatedFAQs);
      console.log('Generated Greeting:', formData.googleBusinessProfile.generatedGreeting);
      console.log('Location:', formData.googleBusinessProfile.location);
      console.log('Opening Hours:', formData.googleBusinessProfile.openingHours);
      console.log('Business Status:', formData.googleBusinessProfile.businessStatus);
      console.log('Price Level:', formData.googleBusinessProfile.priceLevel);
      console.log('Plus Code:', formData.googleBusinessProfile.plusCode);
      console.log('Linked At:', formData.googleBusinessProfile.linkedAt);
    }
    
    console.log('=== CONTACT VERIFICATION ===');
    console.log('Email:', formData.email);
    console.log('Phone Number:', formData.phoneNumber);
    console.log('Email OTP:', formData.emailOtp);
    console.log('Phone OTP:', formData.otp);
    
    console.log('=== EXTRACTED/PROCESSED INFO ===');
    console.log('Extracted Info:', formData.extractedInfo);
    
    console.log('=== FAQs ===');
    console.log('Custom FAQs:', formData.faqs);
    
    console.log('=== BRANDING & AGENT ===');
    console.log('Agent Name:', formData.agentName);
    console.log('Greeting Message:', formData.greeting);
    console.log('Record Disclaimer:', formData.recordDisclaimer);
    console.log('Notification Emails:', formData.notificationEmails);
    console.log('Notification Options:', formData.notificationOptions);
    
    console.log('=== FEATURES ===');
    console.log('Features Configuration:', formData.features);
    
    console.log('=== API PAYLOAD READY ===');
    const apiPayload = {
      // Basic business info
      businessName: formData.businessName,
      websiteAddress: formData.websiteAddress,
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      
      // Google Business Profile flag and data
      isGBP: formData.isGBP,
      googleBusinessProfile: formData.isGBP ? {
        placeId: formData.googleBusinessProfile?.placeId,
        businessName: formData.googleBusinessProfile?.businessName,
        address: formData.googleBusinessProfile?.address,
        phone: formData.googleBusinessProfile?.phone,
        internationalPhone: formData.googleBusinessProfile?.internationalPhone,
        website: formData.googleBusinessProfile?.website,
        industry: formData.googleBusinessProfile?.industry,
        businessOverview: formData.googleBusinessProfile?.businessOverview,
        services: formData.googleBusinessProfile?.services,
        rating: formData.googleBusinessProfile?.rating,
        totalReviews: formData.googleBusinessProfile?.totalReviews,
        businessTypes: formData.googleBusinessProfile?.businessTypes,
        editorialSummary: formData.googleBusinessProfile?.editorialSummary,
        formattedPhoneNumber: formData.googleBusinessProfile?.formattedPhoneNumber,
        internationalPhoneNumber: formData.googleBusinessProfile?.internationalPhoneNumber,
        openingHours: formData.googleBusinessProfile?.openingHours,
        businessStatus: formData.googleBusinessProfile?.businessStatus,
        priceLevel: formData.googleBusinessProfile?.priceLevel,
        location: formData.googleBusinessProfile?.location,
        generatedFAQs: formData.googleBusinessProfile?.generatedFAQs,
        generatedGreeting: formData.googleBusinessProfile?.generatedGreeting
      } : null,
      
      // Agent configuration
      agentName: formData.agentName,
      greeting: formData.greeting,
      recordDisclaimer: formData.recordDisclaimer,
      
      // Features and notifications
      features: formData.features,
      notificationOptions: formData.notificationOptions,
      notificationEmails: formData.notificationEmails,
      
      // Custom FAQs
      faqs: formData.faqs,
      
      // Processed info with all available data
      extractedInfo: formData.extractedInfo
    };
    
    console.log('API Payload (for database save):', JSON.stringify(apiPayload, null, 2));
    console.log('=== END FINAL SUBMISSION LOG ===');
    
    setIsPhoneLoading(true);

    try {
        const result = await onboardingService.saveBusinessOnboarding(apiPayload);
        
        if (result.success) {
          console.log('Onboarding saved successfully:', result.data);
          setCurrentStep(6);
        } else {
          console.error('Failed to save onboarding:', result.error);
          // Show error message to user
          alert('Failed to complete onboarding. Please try again.');
        }
      } catch (error) {
        console.error('Error during final submission:', error);
        /*alert('An error occurred. Please try again.');*/

        const errorMessage = error.message?.includes('network') || error.message?.includes('fetch')
          ? "Network connection error. Please check your internet connection and try again."
          : "An unexpected error occurred during submission. Please try again.";
          
        handleError(
          errorMessage,
          {
            originalError: error.message,
            stack: error.stack,
            timestamp: new Date().toISOString()
          },
          5
        );

      } finally {
        setIsPhoneLoading(false);
      }
   
  };

  const handleNextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 2) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Open the custom modal
  const handleCancel = () => {
    setShowCancelModal(true);
  };

  // Logic to execute after user confirms cancellation
const handleConfirmCancel = () => {
  setShowCancelModal(false);
  setFormData({
    businessName: "",
    websiteAddress: "",
    email: "",
    phoneNumber: "",
    otp: "",
    emailOtp: "",
    extractedInfo: null,
    faqs: [],
    agentName: "",
    greeting: "",
    recordDisclaimer: "This call may be recorded for quality assurance purposes.",
    notificationEmails: "",
    notificationOptions: {
      email: false,
      sms: false,
      whatsapp: false,
      disclaimerEnabled: true,
    },
    features: {
      takingMessages: true,
      bookAppointments: true,
      appointmentReminder: true,
      transferCall: false,
    },
    isGBP: false,
    googleBusinessProfile: null,
  });
  setCurrentStep(1);
  setIsPhoneLoading(false);
  setIsEmailLoading(false);
  setPhoneOtpSent(false);
  setPhoneOtpVerified(false);
  setEmailOtpSent(false);
  setEmailOtpVerified(false);
  setIsTraining(false);
  setIsStepOneValid(false);
  setErrorState({
  hasError: false,
  errorMessage: "",
  errorDetails: null,
  previousStep: 5
});

};
  // Logic to execute after user confirms cancellation
const handleConfirmCancel_2 = () => {
  setShowCancelModal(false);
  
  // Preserve email and phone verification data
  const preservedData = {
    email: formData.email,
    phoneNumber: formData.phoneNumber,
    emailOtp: formData.emailOtp,
    otp: formData.otp,
  };
  
  setFormData({
    // Reset all fields to initial state
    businessName: "",
    websiteAddress: "",
    email: preservedData.email, // Keep email
    phoneNumber: preservedData.phoneNumber, // Keep phone
    otp: preservedData.otp, // Keep phone OTP
    emailOtp: preservedData.emailOtp, // Keep email OTP
    extractedInfo: null,
    faqs: [],
    agentName: "",
    greeting: "",
    recordDisclaimer: "This call may be recorded for quality assurance purposes.",
    notificationEmails: "",
    notificationOptions: {
      email: false,
      sms: false,
      whatsapp: false,
      disclaimerEnabled: true,
    },
    features: {
      takingMessages: true,
      bookAppointments: true,
      appointmentReminder: true,
      transferCall: false,
    },
    isGBP: false,
    googleBusinessProfile: null,
  });
  
  setCurrentStep(1);
  setIsPhoneLoading(false);
  setIsEmailLoading(false);
  // Keep the OTP states intact - don't reset these
  // setPhoneOtpSent(false);
  // setPhoneOtpVerified(false);
  // setEmailOtpSent(false);
  // setEmailOtpVerified(false);
  setIsTraining(false);
  setIsStepOneValid(false);
};
  // Logic to execute after user confirms cancellation
  const handleConfirmCancel_1 = () => {
    setShowCancelModal(false);
    setFormData({
      businessName: "",
      websiteAddress: "",
      email: "",
      phoneNumber: "",
      otp: "",
      emailOtp: "",
      extractedInfo: null,
      faqs: [],
      agentName: "",
      greeting: "",
      recordDisclaimer: "This call may be recorded for quality assurance purposes.",
      notificationEmails: "",
      notificationOptions: {
        email: false,
        sms: false,
        whatsapp: false,
        disclaimerEnabled: true,
      },
      features: {
        takingMessages: true,
        bookAppointments: true,
        appointmentReminder: true,
        transferCall: false,
      },
      isGBP: false, // Reset Google Business Profile flag
      googleBusinessProfile: null, // Reset Google Business Profile data
    });
    setCurrentStep(1);
    setIsPhoneLoading(false);
    setIsEmailLoading(false);
    setPhoneOtpSent(false);
    setPhoneOtpVerified(false);
    setEmailOtpSent(false);
    setEmailOtpVerified(false);
    setIsTraining(false);
    setIsStepOneValid(false);
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  let content;
  switch (currentStep) {
    case 1:
      content = (
        <StepOneForm
          formData={formData}
          handleInputChange={handleInputChange}
          handleSendPhoneOTP={handleSendPhoneOTP}
          handleVerifyPhoneOTP={handleVerifyPhoneOTP}
          handleSendEmailOTP={handleSendEmailOTP}
          handleVerifyEmailOTP={handleVerifyEmailOTP}
          isPhoneLoading={isPhoneLoading}
          isEmailLoading={isEmailLoading}
          phoneOtpSent={phoneOtpSent}
          phoneOtpVerified={phoneOtpVerified}
          emailOtpSent={emailOtpSent}
          emailOtpVerified={emailOtpVerified}
          handleNextStep={handleTrainAgent}
          isTraining={isTraining}
          handleCancel={handleCancel}
          isEmailValid={validateEmail(formData.email)}
          onValidationChange={handleValidationChange}
          emailOtpError={emailOtpError}
          phoneOtpError={phoneOtpError}
          clearEmailOtpError={clearEmailOtpError}
          clearPhoneOtpError={clearPhoneOtpError}
          handleEmailVerification={handleEmailVerification}
          isCheckingEmail={isCheckingEmail}
          emailExistsError={emailExistsError}
          setEmailExistsError={setEmailExistsError}

         
        />
      );
      break;
    case 2:
      content = (
        <StepTwoVerify
          formData={formData}
          handleInputChange={handleInputChange}
          handleSubmit={handleNextStep}
          setCurrentStep={setCurrentStep}
          isLoading={isTraining}
          handleCancel={handleCancel}
        />
      );
      break;
    case 3:
      content = (
        <StepThreeFAQ
          formData={formData}
          handleInputChange={handleInputChange}
          handleNextStep={handleNextStep}
          handlePrevStep={handlePrevStep}
          isLoading={isTraining}
          handleCancel={handleCancel}
        />
      );
      break;
    case 4:
      content = (
        <StepFourBrand
          formData={formData}
          handleInputChange={handleInputChange}
          handleNextStep={handleNextStep}
          handlePrevStep={handlePrevStep}
          isLoading={isTraining}
          handleCancel={handleCancel}
        />
      );
      break;
    case 5:
      content = (
        <StepFiveFeatures
          formData={formData}
          handleInputChange={handleInputChange}
          handleFinalSubmit={handleFinalSubmit}
          handlePrevStep={handlePrevStep}
          isLoading={isPhoneLoading}
          handleCancel={handleCancel}
        />
      );
      break;
    case 6:
      content = (
      <ThankYouPage 
        formData={formData}
      />
    );
      break;
    case 7:
      content = (
        <OnboardingErrorPage
          errorMessage={errorState.errorMessage}
          errorDetails={errorState.errorDetails}
          onTryAgain={handleTryAgain}
          onGoBack={handleGoBack}
          onStartOver={handleStartOver}
          showTryAgain={true}
          showGoBack={errorState.previousStep > 1}
          showStartOver={true}
        />
      );
  break;

    default:
      content = <StepOneForm />;
  }

  return (
    <>
      {content}
      <CancelConfirmationModal
        isOpen={showCancelModal}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
      />
    </>
  );
}